import { format } from 'date-fns'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { notification } from '@last/core-ui/v3/plugins/notification.ts'

import api from '@/api'
import { useLocationsStore } from '@/modules/locations/store/locations.ts'
import type {
  DemoBooking,
  DemoBookingType,
  MissingInfoPayload,
  OnboardingSteps
} from '@/modules/onboarding/types'
import { useAuthStore } from '@/store/auth'

export const useOnboardingStore = defineStore('onboarding', () => {
  const authStore = useAuthStore()
  const { onboarding, user, accessToken } = storeToRefs(authStore)
  const { updateUser, logout, refreshCurrentUser } = authStore

  const locationsStore = useLocationsStore()
  const { organizations, selectedOrganizationId } = storeToRefs(locationsStore)

  const { t } = useI18n()

  const route = useRoute()

  const scrollChildIntoView = ref<(childEl: any) => void>()

  const currentStep = ref<OnboardingSteps>()

  const activeBooking = computed<DemoBooking | undefined>(() =>
    onboarding.value?.bookings.find((b: any) =>
      ['demo-scheduling', 'call-scheduling'].includes(b.bookingType)
    )
  )

  const activeBookingType = computed<DemoBookingType | undefined>(
    () => activeBooking.value?.bookingType
  )

  async function updateMissingInfo(payload: MissingInfoPayload): Promise<void> {
    const { phoneNumber, ...onboardingParams } = payload

    await Promise.allSettled([
      updateUser({ phoneNumber }),
      updateOnboarding({ ...onboardingParams })
    ])
  }

  const loadingSlots = ref(true)
  const availableSlots = ref<any>()
  async function getSlotsForDate(date: Date, mode: 'demo' | 'call'): Promise<void> {
    loadingSlots.value = true

    const { data } = await api.get('/dashboard/onboarding/demo/available-slots', {
      params: {
        day: format(date, 'yyyy-MM-dd'),
        service: `${mode}-scheduling`
      }
    })

    if (!data.slots || !data.slots.length) {
      availableSlots.value = undefined
    } else {
      availableSlots.value = data.slots.map((slot: any) =>
        format(new Date(slot).toUTCString(), 'HH:mm')
      )
    }

    loadingSlots.value = false
  }

  const isLoadingBooking = ref<boolean>(false)
  const demoDateTime = ref('')
  async function bookDemo({
    day,
    hour,
    mode
  }: {
    day: string
    hour: string
    mode: 'demo' | 'call'
  }): Promise<void> {
    isLoadingBooking.value = true
    demoDateTime.value = `${day} ${hour}`
    const UTCDate = new Date(`${day} ${hour}`).toUTCString()

    try {
      if (activeBooking.value) await cancelDemo()
      await api.post(
        '/dashboard/onboarding/demo/book',
        {
          date: UTCDate
        },
        {
          params: {
            service: `${mode}-scheduling`
          }
        }
      )

      await refreshOnboarding()
    } catch (_) {
      notification.create({
        title: t('onboarding.demo-scheduling.error'),
        icon: 'close'
      })
    } finally {
      isLoadingBooking.value = false
    }
  }

  async function cancelDemo() {
    const [team, number] = activeBooking.value?.bookingId?.split('-') || []
    if (team && number && activeBooking.value && activeBookingType.value) {
      await api.delete('/dashboard/onboarding/demo', {
        params: {
          bookingNumber: number,
          bookingTeam: team,
          service: activeBookingType.value
        }
      })
    }
    await updateOnboarding({
      demoDate: null,
      demoId: null,
      demoStaff: null
    })
  }

  function getAnalyticsData() {
    const result: Record<string, string> = {
      Form: 'v2'
    }
    const fields = [
      'form',
      'gclid',
      'marketing_id',
      'referrername',
      'utm_adgroup',
      'utm_adname',
      'utm_campaign',
      'utm_content',
      'utm_medium',
      'utm_source',
      'utm_term'
    ]
    return fields.reduce((res, cookie) => {
      try {
        let value = getCookie(cookie)
        if (value) res[cookie] = value
        if (cookie === 'utm_source' && !value && !!document.referrer) {
          const referrer = document.referrer
          const url = new URL(referrer)
          value = url.hostname.replace('www.', '')
          res[cookie] = value
          res['utm_medium'] = 'organic'
        }
        return res
      } catch {
        return res
      }
    }, result)
  }

  function getCookie(name: string) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop()?.split(';').shift()
  }

  async function refreshOnboarding() {
    await refreshCurrentUser()
    if (!user.value.id) return

    if (
      (selectedOrganizationId.value && organizations.value[selectedOrganizationId.value]?.isDemo) ||
      user.value.isSupport
    ) {
      onboarding.value.completed = true
      return
    }
    try {
      const response = await api.get(`/dashboard/onboarding/user`)
      onboarding.value = response.data
    } catch {
      onboarding.value.completed = true
    }
  }

  async function updateOnboarding(payload: any, options = { silent: false }) {
    if (!options.silent)
      await api.put(`/dashboard/onboarding/user`, {
        update: payload,
        analyticsData: getAnalyticsData()
      })
    onboarding.value = {
      ...onboarding.value,
      ...payload
    }
  }

  const isLoadingOnboarding = ref<boolean>(false)
  watch(
    accessToken,
    async (value: any) => {
      if (value && value !== '' && route.name !== 'signup') {
        isLoadingOnboarding.value = true
        await refreshOnboarding()
        isLoadingOnboarding.value = false
      }
    },
    { immediate: true }
  )

  watch([onboarding, user, isLoadingOnboarding], () => {
    if (isLoadingOnboarding.value) return
    if (
      !onboarding.value?.locationName ||
      !user.value.phoneNumber ||
      !onboarding.value?.locationCity
    ) {
      currentStep.value = 'info'
    } else if (activeBookingType.value) {
      currentStep.value = 'thankyou'
    } else {
      currentStep.value = 'demo'
    }
  })

  return {
    currentStep,
    scrollChildIntoView,
    logout,

    user,
    onboarding,
    updateMissingInfo,

    loadingSlots,
    availableSlots,
    getSlotsForDate,

    activeBooking,
    activeBookingType,
    isLoadingBooking,
    bookDemo,
    cancelDemo,

    updateOnboarding,
    refreshOnboarding,
    getAnalyticsData
  }
})
